// @oasis-permission-inherits: /ai-edits (studio-edits:view)
import { Icon } from "@mirage-web/icons";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { InfiniteQueryPagination } from "~/components/infinite-query-pagination";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { ShadowOperationIdBadge } from "~/modules/agave-shadow-renders/components/shadow-operation-id-badge";
import {
  CallerBadge,
  CreditLabel,
  ModeBadge,
  ProductTypeBadge,
  agaveShadowRenderCallerSearchSchema,
  agaveShadowRenderModeSearchSchema,
  agaveShadowRenderProductTypeSearchSchema,
} from "~/modules/agave-shadow-renders/components/shadow-render-fields";
import { createAgaveShadowProjectPlayback } from "~/modules/agave-shadow-renders/project-playback";
import { fetchAgaveShadowRendersInfiniteQuery } from "~/modules/agave-shadow-renders/queries";
import type {
  AgaveShadowRenderCaller,
  AgaveShadowRenderMode,
  AgaveShadowRenderProductType,
  AgaveShadowRenderSummary,
} from "~/modules/agave-shadow-renders/schemas";
import {
  JerboaProjectWebPlayerPreview,
  type JerboaProjectWebPlayerPreviewHandle,
} from "~/modules/projects/components/jerboa-project-web-player-preview";
import { formatRelativeTime } from "~/modules/videos/utils";

const terminalStatusSchema = z.enum(["complete", "failed"]);
const searchSchema = z.object({
  caller: agaveShadowRenderCallerSearchSchema.optional().default("all").catch("all"),
  mode: agaveShadowRenderModeSearchSchema.optional().default("all").catch("all"),
  page: z.coerce.number().int().positive().optional().default(1).catch(1),
  productType: agaveShadowRenderProductTypeSearchSchema.optional().default("all").catch("all"),
  status: terminalStatusSchema.optional().default("complete").catch("complete"),
});

type AgaveShadowSearch = z.infer<typeof searchSchema>;
type TerminalStatus = z.infer<typeof terminalStatusSchema>;

const callers: AgaveShadowRenderCaller[] = ["ios", "web", "android", "oryx", "unknown"];

const productTypes: AgaveShadowRenderProductType[] = [
  "AI_EDIT",
  "LONG_TO_SHORT",
  "CAPTIONS",
  "MUSIC",
  "VOICEOVER",
  "AVATAR",
  "REAL_ESTATE",
  "PRODUCT_AD",
];

export const Route = createFileRoute("/_dash/ai-edits/agave-shadows")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { caller, mode, page, productType, status } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const targetPageIndex = Math.max(0, page - 1);
  const [currentPageIndex, setCurrentPageIndex] = useState(targetPageIndex);
  const currentFilters = useMemo(
    () => ({
      caller,
      mode,
      productType,
      status,
    }),
    [caller, mode, productType, status]
  );
  const params = useMemo(
    () => ({
      caller: caller === "all" ? undefined : caller,
      includeProjectPlayback: true,
      mode: mode === "all" ? undefined : mode,
      productType: productType === "all" ? undefined : productType,
      requiresSourceVideoAsset: false,
      status,
      terminalOnly: true,
    }),
    [caller, mode, productType, status]
  );

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, error } =
    fetchAgaveShadowRendersInfiniteQuery.useInfiniteQuery(
      {
        params,
      },
      {
        refetchOnWindowFocus: false,
        retry: false,
      }
    );

  const totalPages = data?.pages.length ?? 0;

  useEffect(() => {
    if (targetPageIndex >= totalPages && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [targetPageIndex, totalPages, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (targetPageIndex < totalPages) {
      setCurrentPageIndex(targetPageIndex);
    }
  }, [targetPageIndex, totalPages]);

  const currentPageData = data?.pages[currentPageIndex];
  const isPagePending = !error && (isLoading || currentPageData == null);

  const onPreviousPage = () => {
    const previousPageIndex = Math.max(0, currentPageIndex - 1);
    setCurrentPageIndex(previousPageIndex);
    navigate({ search: { ...currentFilters, page: previousPageIndex + 1 } });
  };

  const onNextPage = async () => {
    if (!hasNextPage && currentPageIndex >= totalPages - 1) {
      return;
    }

    const nextPageIndex = currentPageIndex + 1;
    setCurrentPageIndex(nextPageIndex);
    navigate({ search: { ...currentFilters, page: nextPageIndex + 1 } });
    if (nextPageIndex >= totalPages) {
      await fetchNextPage();
    }
  };

  const onPageChange = (pageIndex: number) => {
    setCurrentPageIndex(pageIndex);
    navigate({ search: { ...currentFilters, page: pageIndex + 1 } });
  };

  const onStatusChange = (value: string) => {
    updateFilters({ status: value as TerminalStatus });
  };

  const onModeChange = (value: string) => {
    updateFilters({ mode: value as AgaveShadowRenderMode | "all" });
  };

  const onProductTypeChange = (value: string) => {
    updateFilters({ productType: value as AgaveShadowRenderProductType | "all" });
  };

  const onCallerChange = (value: string) => {
    updateFilters({ caller: value as AgaveShadowRenderCaller | "all" });
  };

  function updateFilters(overrides: Partial<AgaveShadowSearch>) {
    navigate({
      search: {
        ...currentFilters,
        ...overrides,
        page: 1,
      },
    });
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold">agave shadow</h1>
        {totalPages > 0 && (
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        )}
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={status} onValueChange={onStatusChange}>
            <SelectTrigger className="w-[160px]" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="complete">complete</SelectItem>
              <SelectItem value="failed">failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Mode</Label>
          <Select value={mode} onValueChange={onModeChange}>
            <SelectTrigger className="w-[140px]" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">all</SelectItem>
              <SelectItem value="low">low</SelectItem>
              <SelectItem value="medium">medium</SelectItem>
              <SelectItem value="high">high</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Product Type</Label>
          <Select value={productType} onValueChange={onProductTypeChange}>
            <SelectTrigger className="w-[180px]" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">all</SelectItem>
              {productTypes.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Caller</Label>
          <Select value={caller} onValueChange={onCallerChange}>
            <SelectTrigger className="w-[140px]" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">all</SelectItem>
              {callers.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load Agave shadow renders</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      )}

      {isPagePending && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[9/8] w-full animate-none rounded-md" />
          ))}
        </div>
      )}

      {!isPagePending && currentPageData && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {currentPageData.results.page.map((result) => (
            <ShadowRenderCard
              key={result.id}
              result={result}
              search={{ ...currentFilters, page }}
            />
          ))}
          {currentPageData.results.page.length === 0 && (
            <div className="rounded-md border p-8 text-center text-sm text-muted-foreground md:col-span-2 xl:col-span-3">
              No shadow renders found
            </div>
          )}
        </div>
      )}

      {totalPages > 0 && !isLoading && !isFetchingNextPage && (
        <div className="flex justify-end">
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
}

function ShadowRenderCard({
  result,
  search,
}: {
  result: AgaveShadowRenderSummary;
  search: AgaveShadowSearch;
}) {
  const sourcePlayback = useMemo(
    () =>
      createAgaveShadowProjectPlayback(result, {
        label: "Original project",
        snapshotId: "source-project",
        uri: result.sourceProjectProtoUri,
        dataUrl: result.sourceProjectProtoUrl,
        assetUrlMap: result.sourceProjectAssetUrlMap,
      }),
    [result]
  );
  const shadowPlayback = useMemo(
    () =>
      createAgaveShadowProjectPlayback(result, {
        label: "Shadow project",
        snapshotId: "shadow-project",
        uri: result.projectDataUri,
        dataUrl: result.projectDataUrl,
        assetUrlMap: result.projectAssetUrlMap,
      }),
    [result]
  );

  return (
    <Link
      to="/ai-edits/agave-shadow-renders/$shadowRenderId"
      params={{ shadowRenderId: result.id }}
      search={search}
      className="block min-w-0 overflow-hidden rounded-md border bg-card transition-colors hover:border-foreground/30"
      aria-label={`Open Agave shadow render from ${new Date(result.createdAt).toLocaleString()}`}
    >
      <div className="relative min-w-0">
        <ShadowProjectComparisonPlayer
          sourcePlayback={sourcePlayback}
          shadowPlayback={shadowPlayback}
          shadowFallbackLabel={result.status === "failed" ? "Failed" : "No shadow project"}
        />
        <div className="pointer-events-none absolute left-2 top-2 rounded bg-black/55 px-1.5 py-0.5 text-xs font-medium text-white">
          {formatRelativeTime(result.createdAt)}
        </div>
        <Icon
          name="link-out"
          className="pointer-events-none absolute right-2 bottom-2 size-4 rounded-full bg-black/55 p-0.5 text-white"
        />
      </div>

      <div className="min-w-0 space-y-2 p-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="min-w-0">
            <CreditLabel credits={result.originalMeteredCredits} />
          </div>
          <div className="flex min-w-0 justify-end">
            <CreditLabel credits={result.agaveMeteredCredits} />
          </div>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <ModeBadge mode={result.mode} />
          <CallerBadge caller={result.caller} />
          <ProductTypeBadge productType={result.productType} />
          <ShadowOperationIdBadge value={result.agaveOperationId} className="flex-1 basis-32" />
        </div>
        {result.error && (
          <div className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive">
            {result.error}
          </div>
        )}
      </div>
    </Link>
  );
}

function ShadowProjectComparisonPlayer({
  sourcePlayback,
  shadowPlayback,
  shadowFallbackLabel,
}: {
  sourcePlayback: ReturnType<typeof createAgaveShadowProjectPlayback>;
  shadowPlayback: ReturnType<typeof createAgaveShadowProjectPlayback>;
  shadowFallbackLabel: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sourcePlayerRef = useRef<JerboaProjectWebPlayerPreviewHandle>(null);
  const shadowPlayerRef = useRef<JerboaProjectWebPlayerPreviewHandle>(null);
  const animationFrameRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const [shouldLoadPlayers, setShouldLoadPlayers] = useState(false);
  const [sourceAspectRatio, setSourceAspectRatio] = useState<PlaybackAspectRatio | null>(null);
  const [shadowAspectRatio, setShadowAspectRatio] = useState<PlaybackAspectRatio | null>(null);
  const comparisonDurationMs = useMemo(
    () =>
      getComparisonDurationMs(sourcePlayback?.stats.durationMs, shadowPlayback?.stats.durationMs),
    [shadowPlayback?.stats.durationMs, sourcePlayback?.stats.durationMs]
  );
  const comparisonAspectRatio = useMemo(
    () =>
      getComparisonAspectRatio(
        sourceAspectRatio,
        shadowAspectRatio,
        sourcePlayback?.stats.aspectRatio,
        shadowPlayback?.stats.aspectRatio
      ),
    [
      shadowAspectRatio,
      shadowPlayback?.stats.aspectRatio,
      sourceAspectRatio,
      sourcePlayback?.stats.aspectRatio,
    ]
  );

  const stopPlaybackLoop = useCallback(() => {
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const seekProjects = useCallback((timeMs: number) => {
    sourcePlayerRef.current?.seek(timeMs);
    shadowPlayerRef.current?.seek(timeMs);
  }, []);

  const startPlaybackLoop = useCallback(() => {
    if (animationFrameRef.current !== null) {
      return;
    }
    startedAtRef.current = performance.now();

    const tick = (nowMs: number) => {
      const elapsedMs = Math.max(0, nowMs - startedAtRef.current);
      const targetTimeMs = comparisonDurationMs > 0 ? elapsedMs % comparisonDurationMs : elapsedMs;
      seekProjects(targetTimeMs);
      animationFrameRef.current = window.requestAnimationFrame(tick);
    };

    animationFrameRef.current = window.requestAnimationFrame(tick);
  }, [comparisonDurationMs, seekProjects]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || shouldLoadPlayers) {
      return;
    }

    if (!("IntersectionObserver" in window)) {
      setShouldLoadPlayers(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoadPlayers(true);
          observer.disconnect();
        }
      },
      { rootMargin: "240px" }
    );
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [shouldLoadPlayers]);

  useEffect(() => stopPlaybackLoop, [stopPlaybackLoop]);

  useEffect(() => {
    setSourceAspectRatio(null);
    setShadowAspectRatio(null);
  }, [shadowPlayback, sourcePlayback]);

  const playProjects = () => {
    setShouldLoadPlayers(true);
    startPlaybackLoop();
  };

  const pauseProjects = () => {
    stopPlaybackLoop();
    seekProjects(0);
  };

  return (
    <div
      ref={containerRef}
      className="grid grid-cols-2 overflow-hidden rounded-none bg-black"
      style={{ aspectRatio: `${comparisonAspectRatio * 2}` }}
      onPointerEnter={playProjects}
      onPointerMove={playProjects}
      onPointerLeave={pauseProjects}
      onMouseEnter={playProjects}
      onMouseLeave={pauseProjects}
      onFocus={playProjects}
      onBlur={pauseProjects}
    >
      <ProjectPlaybackPane
        ref={sourcePlayerRef}
        label="Original"
        playback={sourcePlayback}
        fallbackLabel="No source project"
        shouldLoad={shouldLoadPlayers}
        onAspectRatioChange={setSourceAspectRatio}
      />
      <ProjectPlaybackPane
        ref={shadowPlayerRef}
        label="Shadow"
        playback={shadowPlayback}
        fallbackLabel={shadowFallbackLabel}
        shouldLoad={shouldLoadPlayers}
        onAspectRatioChange={setShadowAspectRatio}
      />
    </div>
  );
}

type PlaybackAspectRatio = {
  width: number;
  height: number;
};

const ProjectPlaybackPane = forwardRef<
  JerboaProjectWebPlayerPreviewHandle,
  {
    label: string;
    playback: ReturnType<typeof createAgaveShadowProjectPlayback>;
    fallbackLabel: string;
    shouldLoad: boolean;
    onAspectRatioChange: (aspectRatio: PlaybackAspectRatio) => void;
  }
>(function ProjectPlaybackPane(
  { label, playback, fallbackLabel, shouldLoad, onAspectRatioChange },
  ref
) {
  return (
    <div className="relative flex min-w-0 items-center justify-center overflow-hidden bg-black text-white/70">
      <JerboaProjectWebPlayerPreview
        ref={ref}
        playback={playback}
        fallbackLabel={fallbackLabel}
        shouldLoad={shouldLoad}
        onAspectRatioChange={onAspectRatioChange}
      />
      <div className="absolute bottom-2 left-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
        {label}
      </div>
    </div>
  );
});

function getComparisonDurationMs(...durationValues: Array<number | null | undefined>) {
  const durations = durationValues.filter(
    (duration): duration is number => typeof duration === "number" && duration > 0
  );
  if (durations.length === 0) {
    return 0;
  }
  return Math.min(...durations);
}

function getComparisonAspectRatio(
  ...aspectRatios: Array<{ width: number; height: number } | null | undefined>
) {
  for (const aspectRatio of aspectRatios) {
    if (aspectRatio && aspectRatio.width > 0 && aspectRatio.height > 0) {
      return aspectRatio.width / aspectRatio.height;
    }
  }
  return 9 / 16;
}
